import ConfirmationDialog from "../../shared/ConfirmationDialog";
import ParticipantListItemEdit from "./ParticipantListItemEdit";
import ParticipantListItemView from "./ParticipantListItemView";
import React, { useState } from "react";
import { ParticipantWithId } from "../../../interfaces/IParticipants";

interface ParticipantListItemProps {
  participant: ParticipantWithId;
  onEditParticipant: (participantId: string) => void;
  onRemoveParticipant: (participantId: string) => void;
  isEditingMode?: boolean;
  isCurrentlyEditing?: boolean;
  onSaveEdit?: (participantId: string, updatedData: Partial<ParticipantWithId>) => void;
  onCancelEdit?: () => void;
}

function ParticipantListItem({
  participant,
  onEditParticipant,
  onRemoveParticipant,
  isEditingMode = false,
  isCurrentlyEditing = false,
  onSaveEdit,
  onCancelEdit,
}: ParticipantListItemProps) {
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [editData, setEditData] = useState({
    firstName: participant.firstName,
    lastName: participant.lastName,
    birthDate: participant.birthDate,
    gender: participant.gender,
  });

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    onRemoveParticipant(participant.id!);
    setDeleteDialogOpen(false);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  const handleSaveEdit = () => {
    if (onSaveEdit) {
      onSaveEdit(participant.id!, editData);
    }
  };

  const handleCancelEdit = () => {
    // Reset danych do oryginalnych wartości
    setEditData({
      firstName: participant.firstName,
      lastName: participant.lastName,
      birthDate: participant.birthDate,
      gender: participant.gender,
    });
    if (onCancelEdit) {
      onCancelEdit();
    }
  };

  if (isCurrentlyEditing) {
    return (
      <ParticipantListItemEdit
        editData={editData}
        onChange={(data) => setEditData((prev) => ({ ...prev, ...data }))}
        onSave={handleSaveEdit}
        onCancel={handleCancelEdit}
      />
    );
  }

  return (
    <>
      <ParticipantListItemView
        participant={participant}
        isEditingMode={isEditingMode}
        onEdit={() => onEditParticipant(participant.id!)}
        onDelete={handleDeleteClick}
      />
      <ConfirmationDialog
        open={deleteDialogOpen}
        title="Potwierdź usunięcie zawodnika"
        message={`Czy na pewno chcesz usunąć zawodnika "${participant.firstName} ${participant.lastName}"? Tej operacji nie można cofnąć.`}
        cancelText="Anuluj"
        confirmText="Usuń"
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        confirmColor="error"
      />
    </>
  );
}

export default React.memo(ParticipantListItem, (prev, next) => {
  // Rerender tylko jeśli zmienia się tożsamość/edycja lub dane uczestnika
  if (prev.isEditingMode !== next.isEditingMode) return false;
  if (prev.isCurrentlyEditing !== next.isCurrentlyEditing) return false;
  if (prev.participant.id !== next.participant.id) return false;
  if (prev.participant.firstName !== next.participant.firstName) return false;
  if (prev.participant.lastName !== next.participant.lastName) return false;
  if (
    new Date(prev.participant.birthDate).getTime() !==
    new Date(next.participant.birthDate).getTime()
  )
    return false;
  if (prev.participant.gender !== next.participant.gender) return false;
  return true;
});
